import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Trash2, Star, Sparkles, Loader2, Save, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { photoSrc } from "@/components/Avatar";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

const SECTIONS = [
  ["Basic", [["full_name", "Full Name"], ["dob", "Date of Birth", "date"], ["height", "Height"], ["marital_status", "Marital Status", "select", ["Never Married", "Divorced", "Widowed", "Separated"]], ["mother_tongue", "Mother Tongue"], ["religion", "Religion"], ["community", "Community"], ["sub_community", "Sub-community"], ["country", "Country"], ["state", "State"], ["city", "City"], ["residency_status", "Residency Status"]]],
  ["Education", [["qualification", "Qualification"], ["college", "College/Institute"], ["specialization", "Specialization"], ["graduation_year", "Graduation Year"]]],
  ["Career", [["occupation", "Occupation"], ["industry", "Industry"], ["job_title", "Job Title"], ["company", "Company"], ["work_location", "Work Location"], ["experience", "Experience"], ["income_range", "Income Range"]]],
  ["Lifestyle", [["diet", "Diet", "select", ["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan"]], ["smoking", "Smoking", "select", ["No", "Occasionally", "Yes"]], ["drinking", "Drinking", "select", ["No", "Occasionally", "Yes"]], ["fitness", "Fitness"]]],
  ["Family", [["father_occupation", "Father's Occupation"], ["mother_occupation", "Mother's Occupation"], ["siblings", "Siblings"], ["family_type", "Family Type", "select", ["Nuclear", "Joint"]], ["family_values", "Family Values", "select", ["Traditional", "Moderate", "Liberal"]], ["family_location", "Family Location"]]],
  ["Astrology (optional)", [["dob_time", "Time of Birth"], ["birth_place", "Place of Birth"], ["rashi", "Rashi"], ["nakshatra", "Nakshatra"], ["manglik", "Manglik", "select", ["No", "Yes", "Anshik", "Don't Know"]]]],
];

export default function EditProfile() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("Basic");

  useEffect(() => {
    api.get("/profile/me")
      .then(({ data }) => { setForm(data.profile || {}); setPhotos(data.profile?.photos || []); })
      .catch((e) => { setForm({}); toast.error(apiError(e) || "We couldn't open your profile. Please try again."); });
  }, []);
  if (!form) return <FullSpinner />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setArr = (k, v) => set(k, v.split(",").map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile/me", { data: form });
      await refresh();
      toast.success(`Profile saved · ${data.completion}% complete`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (e, isPrivate) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/photos/upload?is_private=${isPrivate}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(data.photos);
      toast.success("Photo uploaded");
      refresh();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const delPhoto = async (fid) => { const { data } = await api.delete(`/photos/${fid}`); setPhotos(data.photos); refresh(); };
  const makePrimary = async (fid) => { const { data } = await api.post(`/photos/set-primary/${fid}`); setPhotos(data.photos); };

  const aiAbout = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/profile-builder", { kind: "about", raw: { name: form.full_name, occupation: form.occupation, city: form.city, hobbies: form.hobbies, interests: form.interests, family_values: form.family_values, religion: form.religion } });
      if (data.text) { set("about_me", data.text); toast.success("AI draft ready — review and edit before saving"); }
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="edit-profile-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl font-bold text-[#58111A]">Edit Profile</h1><p className="text-sm text-[#605255]">{form.completion || 0}% complete</p></div>
        <button onClick={save} disabled={busy} data-testid="save-profile" className="btn-primary">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}</button>
      </div>

      {/* Photos */}
      <div className="mj-card p-5" data-testid="photos-section">
        <h2 className="font-serif text-xl font-semibold text-[#261A1C] mb-3">Photos</h2>
        <div className="flex flex-wrap gap-3">
          {photos.map((ph) => (
            <div key={ph.id} className="relative w-24 h-28 rounded-xl overflow-hidden border border-[#E8DFD1] bg-[#FAF3E0] group">
              <div className="absolute inset-0 flex items-center justify-center text-[#C59B27]"><Camera className="w-6 h-6 opacity-40" /></div>
              <img src={photoSrc(ph.url)} alt="profile" className="relative w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              {ph.primary && <span className="absolute top-1 left-1 bg-[#C59B27] text-[#3B0A12] text-[9px] font-bold px-1.5 py-0.5 rounded">PRIMARY</span>}
              {ph.private && <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />Private</span>}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition">
                {!ph.primary && <button onClick={() => makePrimary(ph.id)} title="Set primary" className="p-1.5 bg-white rounded-full"><Star className="w-3.5 h-3.5 text-[#C59B27]" /></button>}
                <button onClick={() => delPhoto(ph.id)} title="Delete" className="p-1.5 bg-white rounded-full"><Trash2 className="w-3.5 h-3.5 text-[#BE123C]" /></button>
              </div>
            </div>
          ))}
          <label className="w-24 h-28 rounded-xl border-2 border-dashed border-[#C59B27]/50 flex flex-col items-center justify-center text-[#9E7B1B] cursor-pointer hover:bg-[#FAF3E0]" data-testid="upload-public">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5 mb-1" /><span className="text-[10px]">Add Public</span></>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, false)} />
          </label>
          <label className="w-24 h-28 rounded-xl border-2 border-dashed border-[#781826]/40 flex flex-col items-center justify-center text-[#781826] cursor-pointer hover:bg-[#FAF3E0]" data-testid="upload-private">
            <Lock className="w-5 h-5 mb-1" /><span className="text-[10px]">Add Private</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, true)} />
          </label>
        </div>
      </div>

      {/* About + AI */}
      <div className="mj-card p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-xl font-semibold text-[#261A1C]">About Me</h2>
          <button onClick={aiAbout} disabled={aiBusy} data-testid="ai-about-btn" className="btn-outline text-xs">{aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5" /> Improve with AI</>}</button>
        </div>
        <textarea className="mj-input" rows={4} value={form.about_me || ""} onChange={(e) => set("about_me", e.target.value)} placeholder="Tell your story…" data-testid="about-me-input" />
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div><label className="mj-label">Hobbies (comma separated)</label><input className="mj-input" defaultValue={(form.hobbies || []).join(", ")} onBlur={(e) => setArr("hobbies", e.target.value)} /></div>
          <div><label className="mj-label">Interests (comma separated)</label><input className="mj-input" defaultValue={(form.interests || []).join(", ")} onBlur={(e) => setArr("interests", e.target.value)} /></div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar">
        {SECTIONS.map(([name]) => (
          <button key={name} onClick={() => setTab(name)} className={`shrink-0 text-sm px-4 py-2 rounded-full border ${tab === name ? "bg-[#781826] text-white border-[#781826]" : "bg-white border-[#E8DFD1] text-[#605255]"}`}>{name}</button>
        ))}
      </div>
      {SECTIONS.filter(([n]) => n === tab).map(([name, fields]) => (
        <div key={name} className="mj-card p-5 grid sm:grid-cols-2 gap-3">
          {fields.map(([key, label, type, opts]) => (
            <div key={key}>
              <label className="mj-label">{label}</label>
              {type === "select" ? (
                <select className="mj-input" value={form[key] || ""} onChange={(e) => set(key, e.target.value)}>
                  <option value="">Select</option>{opts.map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="mj-input" type={type || "text"} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} />
              )}
            </div>
          ))}
        </div>
      ))}

      <div className="flex gap-3">
        <button onClick={save} disabled={busy} className="btn-primary flex-1">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Profile"}</button>
        <button onClick={() => nav("/app/preferences")} className="btn-outline flex-1">Set Partner Preferences →</button>
      </div>
    </div>
  );
}
